import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { buildUnifiedAnalyticsTheme } from '~/configs/unifiedAnalyticsTheme';
import MUIProvider from '~/contexts/MUIProvider';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  createTheme,
  useTheme,
} from '@mui/material';

import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import { FONT_STACK_SUISSE_WORKS } from '~/components/brand/brandTokens';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import { CommercialOpsNavigation } from '~/pages/CommercialOps/CommercialOpsPage';

import { useLpcOfferHealth } from '~/hooks/apis/reporting/useLpcOfferHealth';

import type ApiError from '~/types/ApiError';

import HotelEvidence from './HotelEvidence';
import HotelPortfolioTable from './HotelPortfolioTable';
import PerformanceDateRangePicker from './PerformanceDateRangePicker';
import PortfolioGroups from './PortfolioGroups';
import WorkspaceNotes from './WorkspaceNotes';
import { lpcHealthParams } from './navigation';
import {
  GROUP_LABELS,
  filterProperties,
  financialPageContext,
  marginEvidence,
  money,
  propertyGroup,
  sortProperties,
} from './review';
import { unresolvedSourceWarnings } from './sourceIssues';
import type { GroupBy, ReviewWindow } from './types';

function ReviewContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [hotelPickerOpen, setHotelPickerOpen] = useState(false);
  const hotelPickerInput = useRef<HTMLInputElement>(null);
  const params = lpcHealthParams(location.search);
  const cleanSearch = params.toString();
  useEffect(() => {
    if (new URLSearchParams(location.search).toString() !== cleanSearch)
      navigate({ pathname: location.pathname, search: cleanSearch, hash: location.hash }, { replace: true });
  }, [cleanSearch, location.pathname, location.search, location.hash, navigate]);
  const requestedRange = {
    start_date: params.get('from') ?? undefined,
    end_date: params.get('to') ?? undefined,
  };
  const sampleRequested = params.get('sample') === '1';
  const query = useLpcOfferHealth(requestedRange, !sampleRequested);
  const window: ReviewWindow = 'custom';
  const dateRange = sampleRequested ? undefined : query.data?.windows?.custom;
  const rangeLabel = dateRange ? `${dateRange.start_date} to ${dateRange.end_date}` : 'Selected dates';
  const groupBy: GroupBy =
    params.get('groupBy') === 'country' ? 'country' : params.get('groupBy') === 'contact' ? 'contact' : 'all';
  const group = params.get('group') ?? '';
  const search = params.get('search') ?? '';
  const mode = params.get('healthView') === 'hotel' ? 'hotel' : 'portfolio';
  useEffect(() => {
    if (mode === 'hotel' && hotelPickerOpen) hotelPickerInput.current?.focus();
  }, [mode, hotelPickerOpen]);
  const sort = params.get('sort') === 'margin' ? 'margin' : params.get('sort') === 'decline' ? 'decline' : 'name';
  const properties = sampleRequested ? [] : (query.data?.properties ?? []);
  const selected = properties.find((property) => property.property_id === params.get('property')) ?? null;
  const visible = sortProperties(filterProperties(properties, search, groupBy, group), window, sort);
  const groups = [...new Set(properties.map((property) => propertyGroup(property, groupBy)))].sort();
  const margin = marginEvidence(visible, window);
  const sourceWarnings = sampleRequested
    ? []
    : unresolvedSourceWarnings(
        query.data?.source_warnings,
        (mode === 'hotel' ? (selected ? [selected] : []) : visible).map((property) => property.property_id),
      );
  const update = (values: Record<string, unknown>) => {
    const next = lpcHealthParams(location.search);
    const appliedValues = {
      ...('from' in values || 'to' in values ? { from: dateRange?.start_date, to: dateRange?.end_date } : {}),
      ...values,
    };
    Object.entries(appliedValues).forEach(([key, value]) =>
      value == null || value === '' ? next.delete(key) : next.set(key, String(value)),
    );
    navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
  };
  usePageBridge({
    pageId: 'lpc-offer-health',
    view: {
      title: 'LPC Offer Health',
      ready: query.isSuccess && !sampleRequested,
      filters: {
        from: dateRange?.start_date ?? null,
        to: dateRange?.end_date ?? null,
        groupBy,
        group,
        healthView: mode,
        property: selected?.property_id ?? null,
      },
      filterSchema: {
        from: { type: 'date' },
        to: { type: 'date' },
        groupBy: { type: 'enum', values: ['all', 'country', 'contact'] },
        group: { type: 'enum', values: ['', ...groups] },
        healthView: { type: 'enum', values: ['hotel', 'portfolio'] },
        property: { type: 'enum', values: [null, ...properties.map((property) => property.property_id)] },
      },
      snapshotDate: query.data?.report_date ?? null,
      sourceCompletion: query.data?.source_completion ?? 'unavailable',
      ...financialPageContext(visible, selected, mode, window),
      snapshotObservationDate: query.data?.report_date ?? null,
      sourceWarnings: sourceWarnings.map((warning) => `${warning.title}: ${warning.detail}`),
      selectedHotel: selected ? { id: selected.property_id, name: selected.property_name } : null,
      definitions: {
        missing: 'Unavailable is not zero. Source completeness is unverified.',
        attribution: query.data?.attribution_model,
        grouping: 'Commercial contact is not a verified account manager. Territory mapping is unavailable.',
      },
    },
    edit: (changes) => update({ ...changes, ...('groupBy' in changes ? { group: '' } : {}) }),
  });
  const dateRangeControl =
    dateRange && query.data ? (
      <PerformanceDateRangePicker
        range={dateRange}
        reportDate={query.data.report_date}
        onApply={(range) => update({ from: range.start_date, to: range.end_date, window: null })}
      />
    ) : null;
  const failure = query.error as ApiError | null;
  return (
    <Container maxWidth={false} sx={{ py: 2, minHeight: '100%', bgcolor: 'background.default' }}>
      <SectionNavigationLayout navigation={<CommercialOpsNavigation currentTab="lpc-offer-health" />}>
        <Stack gap={2}>
          <Box>
            <Button component={RouterLink} to="/commercial-ops" size="small" sx={{ mb: 1 }}>
              ← Commercial Ops
            </Button>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
              <Box>
                <Typography variant="h4" sx={{ fontFamily: FONT_STACK_SUISSE_WORKS }}>
                  LPC Offer Health
                </Typography>
              </Box>
              <PageChatButton />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Review margin, rate parity and the evidence behind the next hotel check.
            </Typography>
          </Box>
          {sampleRequested && (
            <Alert
              severity="info"
              action={
                <Button color="inherit" onClick={() => update({ sample: null })}>
                  Open live data
                </Button>
              }
            >
              LPC Offer Health has no sample dataset. Open live data to review the reporting snapshot.
            </Alert>
          )}
          {query.isPending && !sampleRequested && (
            <Stack gap={2}>
              <Skeleton variant="rounded" height={90} />
              <Skeleton variant="rounded" height={360} />
            </Stack>
          )}
          {query.isError && !sampleRequested && (
            <Alert
              severity={failure?.status === 503 ? 'warning' : 'error'}
              action={
                <Button
                  color="inherit"
                  onClick={() => (failure?.status === 400 ? update({ from: null, to: null }) : void query.refetch())}
                >
                  {failure?.status === 400 ? 'Reset dates' : 'Retry'}
                </Button>
              }
            >
              <Typography fontWeight={600}>
                {failure?.status === 401 || failure?.status === 403
                  ? 'Your account cannot access LPC evidence.'
                  : 'LPC evidence is unavailable.'}
              </Typography>
              <Typography variant="body2">
                {failure?.status === 503
                  ? 'No completed snapshot is available from the reporting service. A successful backend refresh is needed before figures can be shown.'
                  : failure?.status === 400
                    ? failure.message
                    : 'The reporting service could not return the snapshot. Your filters have been kept.'}
              </Typography>
            </Alert>
          )}
          {query.data && !sampleRequested && (
            <>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
                sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Data through{' '}
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {query.data.report_date}
                  </Box>
                  {' · '}
                  {query.data.eligible_properties} pilot hotels{' · '}Updated{' '}
                  {new Date(query.data.generated_at).toLocaleTimeString('en-AU', {
                    timeZone: 'Australia/Sydney',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}{' '}
                  Sydney
                </Typography>
                <Stack direction="row" gap={1} alignItems="center">
                  <Button size="small" onClick={() => void query.refetch()} disabled={query.isFetching}>
                    {query.isFetching ? 'Checking…' : 'Reload'}
                  </Button>
                </Stack>
              </Stack>
              {query.data.stale && (
                <Alert severity="warning">
                  Showing the last successful snapshot. The expected completed day has not refreshed yet.
                </Alert>
              )}
              <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={mode}
                  onChange={(_, value) => {
                    if (!value) return;
                    update({ healthView: value });
                    setHotelPickerOpen(value === 'hotel' && !selected);
                  }}
                  aria-label="Review workspace"
                  sx={{ alignSelf: 'flex-start', '& .MuiToggleButton-root': { textTransform: 'none', px: 2 } }}
                >
                  <ToggleButton value="portfolio">Portfolio overview</ToggleButton>
                  <ToggleButton
                    value="hotel"
                    onClick={() => {
                      if (!selected) setHotelPickerOpen(true);
                    }}
                  >
                    {selected ? 'Hotel details' : 'Select a hotel'}
                  </ToggleButton>
                </ToggleButtonGroup>
                {mode === 'hotel' && (
                  <Autocomplete
                    size="small"
                    options={[...properties].sort((a, b) => a.property_name.localeCompare(b.property_name))}
                    value={selected}
                    open={hotelPickerOpen}
                    onOpen={() => setHotelPickerOpen(true)}
                    onClose={() => setHotelPickerOpen(false)}
                    onChange={(_, property) => {
                      update({ property: property?.property_id ?? null });
                      setHotelPickerOpen(false);
                    }}
                    getOptionLabel={(property) => property.property_name}
                    isOptionEqualToValue={(a, b) => a.property_id === b.property_id}
                    renderInput={(input) => (
                      <TextField
                        {...input}
                        inputRef={hotelPickerInput}
                        label="Select a hotel"
                        placeholder="Click to select or search"
                      />
                    )}
                    sx={{ width: { xs: '100%', md: 450 }, ml: { md: 'auto' } }}
                  />
                )}
              </Stack>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25}>
                {dateRangeControl}
                {mode === 'portfolio' && (
                  <>
                    <TextField
                      select
                      size="small"
                      label="Group by"
                      value={groupBy}
                      onChange={(event) => update({ groupBy: event.target.value, group: '' })}
                      sx={{ width: 185 }}
                    >
                      {Object.entries(GROUP_LABELS).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {groupBy !== 'all' && (
                      <TextField
                        select
                        size="small"
                        label={GROUP_LABELS[groupBy]}
                        value={groups.includes(group) ? group : ''}
                        onChange={(event) => update({ group: event.target.value })}
                        sx={{ minWidth: 190 }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {groups.map((value) => (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    <TextField
                      size="small"
                      label="Find a hotel"
                      placeholder="Hotel, country or property ID"
                      value={search}
                      onChange={(event) => update({ search: event.target.value })}
                      sx={{ width: { xs: '100%', sm: 320 }, maxWidth: '100%' }}
                    />
                    <TextField
                      size="small"
                      select
                      label="Sort hotels"
                      value={sort}
                      onChange={(event) => update({ sort: event.target.value })}
                      sx={{ minWidth: 180, ml: { md: 'auto' } }}
                    >
                      <MenuItem value="name">Hotel: A to Z</MenuItem>
                      <MenuItem value="margin">LPC margin: high to low</MenuItem>
                      <MenuItem value="decline">7-day LPC margin: largest decline</MenuItem>
                    </TextField>
                  </>
                )}
              </Stack>
              {mode === 'portfolio' &&
                sourceWarnings.map((warning) => (
                  <Alert key={warning.id} severity={warning.severity}>
                    {warning.title}: {warning.detail}
                  </Alert>
                ))}
              {mode === 'portfolio' ? (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          Hotels in view
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.5, fontSize: '2.25rem' }}>
                          {visible.length}
                        </Typography>
                        <Typography variant="caption">{properties.length} hotels included in this pilot</Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          Observed LPC margin · {rangeLabel}
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.5, fontSize: '2.25rem' }}>
                          {money(margin.observedSubtotal)}
                        </Typography>
                        <Typography variant="caption">
                          {margin.total == null ? 'Subtotal · ' : ''}AUD, after refunds · {margin.contributingHotels} of{' '}
                          {margin.selectedHotels} hotels
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="body2" color="text.secondary">
                          Latest-day parity coverage
                        </Typography>
                        <Typography variant="h3" sx={{ mt: 0.5, fontSize: '2.25rem' }}>
                          {visible.filter((property) => property.evidence.parity === 'observed').length} /{' '}
                          {visible.length}
                        </Typography>
                        <Typography variant="caption">Hotels with observations on {query.data.report_date}</Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  <PortfolioGroups
                    properties={filterProperties(properties, search, groupBy, '')}
                    groupBy={groupBy}
                    window={window}
                    onSelect={(value) => update({ group: value })}
                  />
                  <Card variant="outlined">
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        gap={1}
                        flexWrap="wrap"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          Hotels{' '}
                          <Box component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 400 }}>
                            ({visible.length})
                          </Box>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Margins after refunds · AUD · {rangeLabel}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        7-day change: seven days ending {dateRange?.end_date} versus the preceding seven days.
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Click anywhere on a hotel row to open its details. Scroll within the list to see more hotels.
                      </Typography>
                      <HotelPortfolioTable
                        properties={visible}
                        window={window}
                        comparisonEndDate={dateRange?.end_date}
                        onReview={(id) => update({ property: id, healthView: 'hotel' })}
                        onClear={() => update({ group: '', search: '' })}
                      />
                    </CardContent>
                  </Card>
                  <Typography variant="caption" color="text.secondary">
                    Window: {query.data.windows?.[window]?.start_date ?? 'Unavailable'} to{' '}
                    {query.data.windows?.[window]?.end_date ?? 'Unavailable'}.{' '}
                    {query.data.attribution_model ?? 'Rolling attribution evidence is not available in this snapshot.'}
                  </Typography>
                </>
              ) : (
                <>
                  {selected ? (
                    <HotelEvidence
                      key={selected.property_id}
                      property={selected}
                      snapshot={query.data}
                      window={window}
                      dateRange={dateRange}
                    />
                  ) : (
                    <Card variant="outlined">
                      <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
                        <Typography variant="h6" gutterBottom>
                          Select a hotel to view its details
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                          Search for a hotel above, or open any row in the portfolio to review performance, prices and
                          history.
                        </Typography>
                        <Button variant="contained" onClick={() => setHotelPickerOpen(true)}>
                          Choose a hotel
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              {(mode === 'portfolio' || !selected) && (
                <Accordion disableGutters elevation={0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2" fontWeight={600}>
                      Data and definitions
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <WorkspaceNotes />
                  </AccordionDetails>
                </Accordion>
              )}
            </>
          )}
        </Stack>
      </SectionNavigationLayout>
    </Container>
  );
}

function HealthWorkspace() {
  const base = useTheme();
  const theme = useMemo(() => {
    const workspace = buildUnifiedAnalyticsTheme(base);
    return createTheme(workspace, {
      components: {
        // The legacy admin theme makes every Stack scrollable. A fractional line-height
        // can then add a scrollbar to a plain heading row; only the canvas should scroll.
        MuiStack: { styleOverrides: { root: { overflow: 'visible' } } },
        MuiAccordion: {
          styleOverrides: {
            root: {
              backgroundColor: workspace.palette.background.paper,
              '&.Mui-expanded': { backgroundColor: workspace.palette.background.paper },
            },
          },
        },
        MuiAccordionSummary: {
          styleOverrides: {
            root: { padding: workspace.spacing(1.5, 3) },
            content: { margin: workspace.spacing(0.5, 0), '&.Mui-expanded': { margin: workspace.spacing(0.5, 0) } },
          },
        },
        MuiAccordionDetails: {
          styleOverrides: {
            root: {
              padding: workspace.spacing(2.5, 3, 3),
              overflow: 'visible',
              borderTop: `1px solid ${workspace.palette.divider}`,
              backgroundColor: workspace.palette.background.paper,
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              backgroundColor: workspace.palette.action.hover,
              color: workspace.palette.text.secondary,
              fontWeight: 600,
            },
            // A transparent sticky header lets the scrolling row text show through it.
            stickyHeader: { backgroundColor: workspace.palette.background.paper, zIndex: 2 },
          },
        },
      },
    });
  }, [base]);
  return (
    <ThemeProvider theme={theme}>
      <PageChatLayout>
        <ReviewContent />
      </PageChatLayout>
    </ThemeProvider>
  );
}

export default function LPCOfferHealthPage() {
  return (
    <MUIProvider>
      <HealthWorkspace />
    </MUIProvider>
  );
}
